import {
  ItemStatus,
  Job,
  JobStatus,
  PaginatedJobResponse,
} from '../generated/gql-types';
import { getApolloClient } from '../lib/getApolloClient';
import {
  APPLY_FOR_JOB,
  GET_ALL_POTENTIAL_JOBS_FOR_USER_ID,
  GET_JOB,
  ACCEPT_JOB,
  GET_ALL_JOBS_BY_STATUS_FOR_USER_ID,
  WITHDRAW_FROM_JOB,
} from '../job/job.gql';

const getAllPotentialJobsForUserId = async (): Promise<Job[]> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_ALL_POTENTIAL_JOBS_FOR_USER_ID,
    });
    if (data.getAllPotentialJobsForUserId) {
      return data.getAllPotentialJobsForUserId;
    }
    return [];
  } catch (error) {
    console.error('Error fetching potential jobs:', error);
    return [];
  }
};

const getAllJobsByStatusForUserId = async (
  status?: JobStatus[],
  userId?: string,
  limit?: number,
  lastEvaluatedKey?: string,
): Promise<PaginatedJobResponse> => {
  try {
    const response = await getApolloClient().query({
      query: GET_ALL_JOBS_BY_STATUS_FOR_USER_ID,
      variables: {
        status,
        ...(userId && { userId }),
        ...(limit && { limit }),
        ...(lastEvaluatedKey && { lastEvaluatedKey }),
      },
    });
    if (response.data.getAllJobsByStatusForUserId) {
      return response.data.getAllJobsByStatusForUserId;
    }
    return {
      hasMore: false,
      jobs: [],
    };
  } catch (error) {
    console.error('Error fetching jobs by status:', error);
    return {
      hasMore: false,
      jobs: [],
    };
  }
};

const getJobById = async (jobId: string): Promise<Job | null> => {
  try {
    const { data } = await getApolloClient().query({
      query: GET_JOB,
      variables: { id: jobId },
    });
    if (data.getJob) {
      return data.getJob;
    }
    return null;
  } catch (error) {
    console.error('Error fetching job by id:', error);
    return null;
  }
};

const acceptJob = async (id: string): Promise<any> => {
  try {
    const response = await getApolloClient().mutate({
      mutation: ACCEPT_JOB,
      variables: { id },
    });
    return response;
  } catch (error) {
    console.error('Error accepting job:', error);
    return {
      errorCode: 'ACCEPT_JOB_FAILED',
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

const applyForJob = async (id: string, coverLetter?: string): Promise<any> => {
  try {
    const trimmedCoverLetter = coverLetter?.trim();
    const response = await getApolloClient().mutate({
      mutation: APPLY_FOR_JOB,
      variables: {
        id,
        ...(trimmedCoverLetter ? { coverLetter: trimmedCoverLetter } : {}),
      },
    });
    return response;
  } catch (error) {
    console.error('Error applying for job:', error);
    return {
      errorCode: 'APPLY_FOR_JOB_FAILED',
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

const withdrawFromJob = async (
  jobId: string,
  reason?: string,
): Promise<any> => {
  try {
    const response = await getApolloClient().mutate({
      mutation: WITHDRAW_FROM_JOB,
      variables: { id: jobId, reason },
    });
    return response;
  } catch (error) {
    console.error('Error withdrawing from job:', error);
    return null;
  }
};

export const workerJobActions = {
  getAllPotentialJobsForUserId,
  getAllJobsByStatusForUserId,
  getJobById,
  acceptJob,
  applyForJob,
  withdrawFromJob,
};
